<template>
  <b-modal
    ref="customNotificationsModal"
    size="md"
    :title="$t('diagnosis.notificationsModal.title')"
    centered
    modal-class="sent-notification-modal"
    qa="send-notification-modal"
    @hidden="onHide"
    @shown="onShown"
  >
    <b-container v-if="!changeView">
      <custom-sent-notifications-grid
        :row-data="customSentNotifications"
        :cas-id="caseId"
        :lab-id="laboratoryId"
        @changeView="changeView = !changeView"
      />
    </b-container>
    <b-container v-if="changeView">
      <b-row class="header-zone">
        <b-col>
          <div class="d-flex align-items-center">
            <span
              v-if="isPredefined"
              class="ap-title ap-title__main title-truncate"
            >
              {{ predefinedNotificationTitle }}
            </span>
            <span
              v-else
              class="ap-title ap-title__main"
            >
              {{ $t('diagnosis.notificationsModal.customNotification') }}
            </span>
            <b-button
              id="pop-notification-type"
              qa="send-notification-modal-change-button"
              class="btn__link px-3"
            >
              {{ $t('diagnosis.notificationsModal.modify') }}
              <i
                class="ml-1 far fa-chevron-down"
                aria-hidden="true"
              />
            </b-button>
            <b-popover
              ref="sendNotificationModalPredefinedPopover"
              placement="rightbottom"
              target="pop-notification-type"
              triggers="click blur"
              @show="getAllActivePredefinedCustomNotifications"
            >
              <div class="ap-popover">
                <ul>
                  <li>
                    <b-button @click="resetForm">
                      {{ $t('diagnosis.notificationsModal.customNotification') }}
                    </b-button>
                  </li>
                  <div class="horizontal-divider my-1" />
                  <li
                    v-for="notification in predefinedCustomNotifications"
                    :key="notification.id"
                  >
                    <b-button @click="onPredefinedCustomNotificationClick(notification)">
                      {{ notification.title }}
                    </b-button>
                  </li>
                </ul>
              </div>
            </b-popover>
          </div>
        </b-col>
        <b-col cols="auto">
          <div>
            <b-button
              qa="send-notification-modal-show-sent-button"
              class="btn__link px-3"
              @click="showSentNotificationsSidepanel"
            >
              <i
                class="fal fa-paper-plane mr-1"
                aria-hidden="true"
              />
              {{ $t('diagnosis.notificationsModal.notificationsSentButton') }}
              <span class="badge vt-nav-badge indicator-bubble badge-secondary">
                {{ customSentNotifications.length }}
              </span>
            </b-button>
          </div>
        </b-col>
      </b-row>
      <b-row class="mt-2">
        <b-col>
          <div class="form-label">
            <label>{{ $t('diagnosis.notificationsModal.subject') }}</label>
          </div>
          <div
            class="text-zone full-bordered"
            :class="{ requiredform: (showErrors && !subject.length) }"
          >
            <div
              v-if="!isPredefined"
              class="identifiers bottom-bordered py-2 px-3"
            >
              <div class="id-zone">
                <b-badge
                  v-for="notificationVariable in customNotificationVariables"
                  :key="notificationVariable.code"
                  :qa="`send-notification-modal-subject-${notificationVariable.code}-span`"
                  @click="appendVariableCodeToSubject(notificationVariable.code)"
                >
                  {{ notificationVariable.translatedCode }}
                </b-badge>
              </div>
            </div>
            <vt-input
              ref="subjectInput"
              v-model="subject"
              v-validate="'required'"
              qa="send-notification-modal-subject-input"
              :disabled="isPredefined"
              :qa-value="subject"
              class="form-control-sm"
              name="customNotificationSubject"
            />
          </div>
        </b-col>
      </b-row>
      <b-row class="mt-3">
        <b-col>
          <div class="form-label">
            <label>{{ $t('diagnosis.notificationsModal.notificationText') }}</label>
          </div>
          <div
            class="text-zone full-bordered"
            :class="{ requiredform: (showErrors && !body.length) }"
          >
            <div
              v-if="!isPredefined"
              class="controls bottom-bordered d-flex justify-content-between align-items-center"
            >
              <div>
                <b-button
                  id="pop-templates"
                  qa="send-notification-modal-load-template-button"
                  class="btn__link px-3 py-2"
                >
                  {{ $t('diagnosis.notificationsModal.loadTemplate') }}<i
                    class="ml-1 far fa-chevron-down"
                    aria-hidden="true"
                  />
                </b-button>
                <b-popover
                  ref="sendNotificationModalTemplatesPopover"
                  target="pop-templates"
                  triggers="click blur"
                  @show="getAllCustomNotificationUserTextTemplates"
                >
                  <div class="ap-popover">
                    <ul v-if="customNotificationTextTemplates.length">
                      <li
                        v-for="textTemplate in customNotificationTextTemplates"
                        :key="textTemplate.id"
                      >
                        <b-button @click="addTextTemplate(textTemplate)">
                          {{ textTemplate.title }}
                        </b-button>
                      </li>
                    </ul>
                    <div
                      v-if="!customNotificationTextTemplates.length"
                      class="py-2 px-3"
                    >
                      <span>{{ $t('diagnosis.notificationsModal.noTemplatesAvailable') }}</span>
                    </div>
                  </div>
                </b-popover>
                <b-button
                  qa="send-notification-modal-templates-list-button"
                  class="btn__icon btn__icon--primary  pl-1"
                  @click="showTemplatesList"
                >
                  <i
                    v-tooltip="$t('diagnosis.notificationsModal.templatesList')"
                    class="far fa-list"
                    aria-hidden="true"
                  />
                </b-button>
              </div>
              <div>
                <b-button
                  qa="send-notification-modal-save-template-button"
                  class="btn__icon btn__icon--primary  px-2"
                  @click="showSaveNotificationTemplateModal"
                >
                  <i
                    v-tooltip="$t('diagnosis.notificationsModal.saveTemplate')"
                    class="far fa-save"
                    aria-hidden="true"
                  />
                </b-button>
                <b-button
                  qa="send-notification-modal-clear-text-button"
                  class="btn__icon btn__icon--primary  pl-2 pr-3"
                  @click="body = ''"
                >
                  <i
                    v-tooltip="$t('diagnosis.notificationsModal.cleanText')"
                    class="far fa-eraser"
                    aria-hidden="true"
                  />
                </b-button>
              </div>
            </div>
            <div
              v-if="!isPredefined"
              class="identifiers bottom-bordered py-2 px-3"
            >
              <div class="id-zone">
                <b-badge
                  v-for="notificationVariable in customNotificationVariables"
                  :key="notificationVariable.code"
                  :qa="`send-notification-modal-${notificationVariable.code}-span`"
                  @click="appendVariableCodeToBody(notificationVariable.code)"
                >
                  {{ notificationVariable.translatedCode }}
                </b-badge>
              </div>
            </div>
            <div class="text-box">
              <vt-rich-text-editor
                ref="notificationBodyTextInput"
                v-model="body"
                v-validate="'required'"
                qa="send-notification-modal-text-textarea"
                class="mt-1"
                autofocus
                input-id="notificationBodyTextInput"
                name="notificationBodyTextInput"
                :allow-images="false"
                :allow-toolbar="false"
                :disabled="isPredefined"
              />
            </div>
          </div>
        </b-col>
      </b-row>
      <b-row>
        <b-col class="d-flex justify-content-end mt-2">
          <div class="switch-btn mt-1 p-0 d-flex align-items-center">
            <span class="mr-2">{{ $t('diagnosis.notificationsModal.attachReport') }}</span>
            <input
              id="toggle-report"
              v-model="attachReport"
              :disabled="isPredefined"
              type="checkbox"
              class="offscreen"
              aria-hidden="true"
            >
            <label
              qa="send-notification-modal-switch-report-label"
              :qa-value="attachReport ? attachReport : 'false'"
              for="toggle-report"
              class="switch mb-0"
            />
          </div>
        </b-col>
      </b-row>
      <b-row class="mt-4 destination-zone">
        <b-col cols="12">
          <span class="ap-title ap-title__section">
            <i
              class="far fa-user-friends mr-2"
              aria-hidden="true"
            />
            {{ $t('diagnosis.notificationsModal.recipients') }}
          </span>
        </b-col>
        <b-col
          class="mt-2"
          cols="12"
        >
          <div class="d-flex mb-3">
            <b-form-checkbox
              v-model="shouldNotifyPetitioner"
              qa="send-notification-modal-switch-petitioner-div"
              :disabled="isPredefined"
              :qa-value="shouldNotifyPetitioner ? shouldNotifyPetitioner : 'false'"
              :class="{ requiredform: (showErrors && !hasRecipients) }"
            >
              <span>{{ $t('diagnosis.notificationsModal.requester') }}</span>
            </b-form-checkbox>
            <b-form-checkbox
              v-model="shouldNotifyDepartment"
              qa="send-notification-modal-switch-unit-div"
              :disabled="isPredefined"
              :qa-value="shouldNotifyDepartment ? shouldNotifyDepartment: 'false'"
              :class="{ requiredform: (showErrors && !hasRecipients) }"
              class="ml-4"
            >
              <span>{{ $t('diagnosis.notificationsModal.requesterUnit') }}</span>
            </b-form-checkbox>
          </div>
        </b-col>
        <b-col>
          <b-form-group>
            <div class="form-label">
              <label>{{ $t('diagnosis.notificationsModal.serviceResponsibilities') }}</label>
            </div>
            <vt-combo-select
              v-model="responsibilitiesToNotifySelected"
              qa="send-notification-modal-service-responsibilities-div"
              :disabled="isPredefined"
              :option-loader="loadResponsibilities"
              label="translatedName"
              track-by="id"
              :open-direction="'top'"
              :multiple="true"
            />
          </b-form-group>
        </b-col>
        <b-col>
          <b-form-group>
            <div class="form-label">
              <label>{{ $t('diagnosis.notificationsModal.servicePersonnel') }}</label>
            </div>
            <vt-combo-select
              v-model="personnelToBeNotifiedSelected"
              qa="send-notification-modal-service-personnel-div"
              :disabled="isPredefined"
              :option-loader="getPersonnel"
              label="fullName"
              track-by="id"
              :open-direction="'top'"
              :multiple="true"
            />
          </b-form-group>
        </b-col>
      </b-row>
    </b-container>
    <div
      slot="modal-footer"
      class="footer-banner px-2"
      style="width:100%;"
    >
      <div class="row">
        <div class="col-6 text-left">
          <b-button
            qa="send-notification-modal-cancel-button"
            size="sm"
            variant="outline-secondary"
            data-dismiss="modal"
            @click="hide"
          >
            {{ $t('global.cancel') }}
          </b-button>
        </div>
        <div class="col-6 text-right">
          <b-button
            v-if="changeView && $_canEmitNotificationToCase()"
            qa="send-notification-modal-send-button"
            size="sm"
            class="btn--primary"
            @click="sendNotification"
          >
            <i
              class="fal fa-paper-plane mr-2"
              aria-hidden="true"
            />
            {{ $t('diagnosis.notificationsModal.sendButton') }}
          </b-button>
        </div>
      </div>
    </div>
    <modal-wrapper ref="saveNotificationTemplateModalWrapper">
      <save-notification-template-modal
        ref="saveNotificationTemplateModal"
        @createTemplate="createCustomNotificationTextTemplate"
        @updateTemplate="patchCustomNotificationTextTemplateBatch"
      />
    </modal-wrapper>
    <notification-templates-list-modal
      ref="notificationTemplatesListModal"
      :templates-list="customNotificationTextTemplates"
      @selectTemplate="addTextTemplate($event)"
      @deleteTemplates="deleteTextTemplates($event)"
    />
  </b-modal>
</template>

<script>
import SaveNotificationTemplateModal from '@/modules/diagnosisModule/views/form/modals/SaveNotificationTemplateModal'
import NotificationTemplatesListModal from '@/modules/diagnosisModule/views/form/modals/NotificationTemplatesListModal'
import CustomSentNotificationsGrid from '@/modules/diagnosisModule/views/grid/customSentNotifications/customSentNotificationsGrid'
import customNotificationsService from '@/api/resources/customNotifications/customNotificationsService'
import { customNotificationVariablesService } from '@/api/resources/customNotifications/customNotificationVariablesService'
import predefinedCustomNotificationsService from '@/api/resources/customNotifications/predefinedCustomNotifications/predefinedCustomNotificationsService'
import customNotificationTextTemplatesService from '@/api/resources/customNotifications/customNotificationTextTemplates/customNotificationTextTemplatesService'
import { personnelService } from '@/api/resources/personnelService'
import responsibilityService from '@/api/resources/responsibilityService'
import { CaseOperationGuardMixin } from '@/api/operations/mixins/guards/case-operation-guard.mixin'

export default {
    name: 'CustomNotificationsModal',
    components: {
        CustomSentNotificationsGrid,
        SaveNotificationTemplateModal,
        NotificationTemplatesListModal
    },
    mixins: [
        CaseOperationGuardMixin
    ],
    props: {
        caseId: Number,
        laboratoryId: Number,
        type: Object,
        sample: Object,
        isCaseValidated: Boolean
    },
    data() {
        return {
            isPredefined: false,
            changeView: true,
            predefinedCustomNotifications: [],
            predefinedCustomNotificationId: null,
            customSentNotifications: [],
            customNotificationVariables: [],
            customNotificationTextTemplates: [],
            personnelToBeNotifiedSelected: [],
            responsibilitiesToNotifySelected: [],
            shouldNotifyPetitioner: false,
            shouldNotifyDepartment: false,
            selectedTextTemplate: {},
            textTemplateTitle: '',
            subject: '',
            body: '',
            showErrors: false,
            predefinedNotificationTitle: '',
            attachReport: false
        }
    },
    computed: {
        hasRecipients() {
            return this.shouldNotifyPetitioner || this.shouldNotifyDepartment
        }
    },
    methods: {
        async show(showSentNotificationsModalSidepanel = false) {
            await this.getAllCustomSentNotifications()
            await this.getAllCustomNotificationVariablesByCase()
            if (showSentNotificationsModalSidepanel) {
                await this.showSentNotificationsSidepanel()
            }
            this.$refs.customNotificationsModal.show()
        },
        showSaveNotificationTemplateModal() {
            this.$refs.saveNotificationTemplateModalWrapper.show([this.textTemplateTitle, this.selectedTextTemplate.id])
        },
        onShown() {
            if (this.changeView) {
                this.$nextTick(() => {
                    this.$refs.subjectInput.focus()
                })
            }
        },
        appendVariableCodeToSubject(code) {
            this.subject += ` {{${code}}}`
            this.$refs.subjectInput.focus()
        },
        appendVariableCodeToBody(code) {
            const variable = `{{${code}}}`
            this.$refs.notificationBodyTextInput.insertTextAtCaretPosition(variable)
            this.$nextTick(() => {
                this.$refs.notificationBodyTextInput.focus()
            })
        },
        closeSendNotificationModalPopovers() {
            if (this.$refs.sendNotificationModalTemplatesPopover) {
                this.$refs.sendNotificationModalTemplatesPopover.$emit('close')
            }
            if (this.$refs.sendNotificationModalPredefinedPopover) {
                this.$refs.sendNotificationModalPredefinedPopover.$emit('close')
            }
        },
        addTextTemplate(textTemplate) {
            this.closeSendNotificationModalPopovers()
            this.body = textTemplate.text
            this.textTemplateTitle = textTemplate.title
            this.selectedTextTemplate = textTemplate
        },
        async deleteTextTemplates(templateIds) {
            await this.deleteCustomNotificationTextTemplateBatch(templateIds)
        },
        resetForm() {
            this.closeSendNotificationModalPopovers()
            this.subject = ''
            this.body = ''
            this.isPredefined = false
            this.personnelToBeNotifiedSelected = []
            this.responsibilitiesToNotifySelected = []
            this.shouldNotifyPetitioner = false
            this.shouldNotifyDepartment = false
            this.attachReport = false
        },
        onPredefinedCustomNotificationClick(notification) {
            this.closeSendNotificationModalPopovers()
            this.isPredefined = true
            this.subject = notification.subject
            this.body = notification.body
            this.shouldNotifyPetitioner = notification.shouldNotifyPetitioner
            this.shouldNotifyDepartment = notification.shouldNotifyDepartment
            this.responsibilitiesToNotifySelected = notification.responsibilitiesToNotify
            this.personnelToBeNotifiedSelected = notification.personnelToBeNotifiedList
            this.predefinedCustomNotificationId = notification.id
            this.predefinedNotificationTitle = notification.title
            this.attachReport = notification.attachReport
        },
        async showSentNotificationsSidepanel() {
            this.changeView = false
        },
        async showTemplatesList() {
            await this.getAllCustomNotificationUserTextTemplates()
            this.$refs.notificationTemplatesListModal.show()
        },
        onHide() {
            this.closeSendNotificationModalPopovers()
            this.$_cleanComponentData()
        },
        hide() {
            this.$refs.customNotificationsModal.hide()
        },
        async getAllActivePredefinedCustomNotifications() {
            const [error, response] = await this.toCall(predefinedCustomNotificationsService.getAllActivePredefinedCustomNotifications(this.laboratoryId, this.type.code))
            if (error) {
                this.showErrorModalGlobal()
                return
            }
            this.predefinedCustomNotifications = response
        },
        async getAllCustomNotificationUserTextTemplates() {
            const [error, response] = await this.toCall(customNotificationTextTemplatesService.getAllCustomNotificationTextTemplatesIncludingLaboratory(this.laboratoryId, this.type.code))
            if (error) {
                this.showErrorModalGlobal()
                return
            }
            this.customNotificationTextTemplates = response
        },
        async createCustomNotificationTextTemplate(event) {
            const createRequest = {
                active: true,
                text: this.body,
                title: event.title,
                type: this.type.code
            }
            const [error] = await this.toCall(customNotificationTextTemplatesService.createCustomNotificationTextTemplate(createRequest))
            if (error) {
                this.showErrorModalGlobal()
                return
            }
            this.textTemplateTitle = createRequest.title
            this.showToastSuccessGlobal()
        },
        async patchCustomNotificationTextTemplateBatch(event) {
            const updateRequest = {
                active: true,
                id: event.textTemplateId,
                text: this.body,
                title: event.title,
                type: this.type.code
            }
            const [error, data] = await this.toCall(customNotificationTextTemplatesService.patchCustomNotificationTextTemplateBatch([updateRequest]))
            if (error) {
                this.showErrorModalGlobal()
                return
            } else if (data.length) {
                this.handleErrors(data)
                return
            }
            this.textTemplateTitle = updateRequest.title
            this.showToastSuccessGlobal()
        },
        async deleteCustomNotificationTextTemplateBatch(templateIds) {
            const request = {
                entityIdentifiers: templateIds
            }
            const [error, data] = await this.toCall(customNotificationTextTemplatesService.deleteCustomNotificationTextTemplateBatch(request))
            if (error) {
                this.showErrorModalGlobal()
                return
            } else if (data.length) {
                this.handleErrors(data)
                return
            }
            this.textTemplateTitle = ''
            this.selectedTextTemplate = {}
            this.showToastSuccessGlobal()
        },
        async sendNotification() {
            const validForm = await this.$validator.validate()
            if (validForm && this.hasRecipients) {
                if (!this.isPredefined) {
                    await this.sendTailoredCustomNotification()
                } else {
                    await this.sendPredefinedCustomNotification()
                }
            } else {
                this.showErrorModalGlobal(this.$t('studyRequest.general.requiredFieldsNotice'))
                this.showErrors = true
            }
        },
        async sendTailoredCustomNotification() {
            const personnelToNotifyIds = this.personnelToBeNotifiedSelected.map(personnel => personnel.id)
            const responsibilitiesToNotifyCodes = this.responsibilitiesToNotifySelected.map(responsibility => responsibility.code)
            const sendRequest = {
                body: this.body,
                personnelToNotifyIds: personnelToNotifyIds,
                responsibilitiesToNotify: responsibilitiesToNotifyCodes,
                shouldNotifyDepartment: this.shouldNotifyDepartment,
                shouldNotifyPetitioner: this.shouldNotifyPetitioner,
                subject: this.subject
            }
            const [err] = await this.toCall(customNotificationsService.sendTailoredCustomNotification(this.laboratoryId, this.caseId, sendRequest, this.attachReport))
            if (err) {
                this.showErrorModalGlobal()
                return
            }
            this.showToastSuccessGlobal(this.$t('diagnosis.notificationsModal.notificationSent'))
            this.$emit('customSentNotificationsUpdated')
            this.hide()
        },
        async sendPredefinedCustomNotification() {
            const sendRequest = {
                predefinedCustomNotificationId: this.predefinedCustomNotificationId
            }
            const [err] = await this.toCall(customNotificationsService.sendPredefinedCustomNotification(this.laboratoryId, this.caseId, sendRequest))
            if (err) {
                this.showErrorModalGlobal()
                return
            }
            this.showToastSuccessGlobal(this.$t('diagnosis.notificationsModal.notificationSent'))
            this.$emit('customSentNotificationsUpdated')
            this.hide()
        },
        async getAllCustomNotificationVariablesByCase() {
            this.updateLoading(true)
            const [err, response] = await this.toCall(customNotificationVariablesService.getAllCustomNotificationVariablesByCase(this.laboratoryId, this.caseId))
            this.updateLoading(false)
            if (err) {
                this.showErrorModalGlobal()
                return
            }
            this.customNotificationVariables = response
        },
        async getAllCustomSentNotifications() {
            this.updateLoading(true)
            const [err, response] = await this.toCall(customNotificationsService.getAllCustomSentNotifications(this.laboratoryId, this.caseId))
            this.updateLoading(false)
            if (err) {
                this.showErrorModalGlobal()
                return
            }
            this.customSentNotifications = response
        },
        async loadResponsibilities() {
            return responsibilityService.findAllResponsibilitiesByLaboratory(this.laboratoryId)
                .then(response => response.sort((a, b) => a.translatedName.localeCompare(b.translatedName)))
        },
        async getPersonnel() {
            return personnelService.getPersonnel(this.laboratoryId)
        }
    }
}
</script>

<style lang="scss">
    .sent-notification-modal .requiredform {
        border-color: red !important;
    }
    .requiredform label span {
        color: red;
    }
    .sent-notification-modal .info-block {
        line-height: 16px;
    }
    .sent-notification-modal .info-block span {
        font-size: font-size(very-small);
    }
    .sent-notification-modal .sidepanel-subject {
        font-size: font-size(normal);
    }
    .sent-notification-modal .sidepanel-date {
        font-size: font-size(small);
    }
    .sent-notification-modal .sidepanel-text {
        font-size: font-size(very-small);
        color: vt-color(dark-text);
        margin-top: 10px;
    }
    .sent-notification-modal .sent-notification-sidepanel {
        position: absolute;
        width: 350px;
        height: calc(100% - 32px);
        background: #FFF;
        z-index: 1;
        right: 0;
        box-shadow: -5px 0 5px -5px #b2b2b2;
        border: 1px solid vt-color(grid-general-border);
        overflow-y: auto;
    }
    .sent-notification-modal .sent-notification-sidepanel .title span {
        color: vt-color(primary) !important;
    }
    .sent-notification-modal .btn-link:focus {
        text-decoration: none;
    }
    .sent-notification-modal .sent-notification-grid {
        height: calc(100vh - 380px);
    }
    .sent-notification-modal .vt-nav-badge {
        left: 5px;
    }
    .sent-notification-modal .destination-zone .multiselect__tags {
        height: 100px;
        overflow-y: auto;
    }
    .sent-notification-modal .destination-zone .multiselect__tags span {
        color: vt-color(white-light);
    }
    .sent-notification-modal .destination-zone .custom-checkbox span {
        font-size: font-size(very-small);
    }
    .sent-notification-modal h5 {
        margin-bottom: 0;
    }
    .sent-notification-modal .title-truncate {
        @include text-truncate($maxWidth: 380px);
    }
    .sent-notification-modal .text-zone {
        border: 1px solid #707070;
        border-radius: .2rem;
    }
    .sent-notification-modal .text-zone textarea.form-control {
        border: none;
        height: 170px;
    }
    .sent-notification-modal .text-zone input {
        border: none;
        padding: 10px;
        font-size: 1rem;
    }
    .sent-notification-modal .controls {
        border-bottom: 1px solid #707070;
    }
    .sent-notification-modal .identifiers {
        border-bottom: 1px solid vt-color(general-border);
        background: vt-color(dark-text, .98);
    }
    .sent-notification-modal .id-zone .badge {
        background: vt-color(alt-primary);
        color: #FFF;
        cursor: pointer;
        transition: .2s;
        margin-right: 5px;
        font-size: font-size(small);
    }
    .sent-notification-modal .id-zone .badge:hover,
    .sent-notification-modal .id-zone .badge.active {
        background: vt-color(primary);
    }
    .sent-notification-pop {
        padding: 0 !important;
    }
    .sent-notification-pop ul {
        padding: 0;
        margin: 5px 0;
    }
    .sent-notification-pop ul li {
        list-style: none;
        padding: 2px 20px;
        font-size: 12px;
        cursor: pointer;
    }
    .sent-notification-pop ul li:hover {
        background: vt-color(light-primary);
    }
    .sent-notification-pop .pop-header {
        background: vt-color(base-text, .9);
        padding: 8px 12px;
        font-weight: 700;
    }
    .sent-notification-modal .modal-body {
        height: calc(100vh - 290px);
        overflow-y: auto;
        overflow-x: hidden;
    }
    .sent-notification-modal .save-notification-template .modal-body,
    .sent-notification-modal .notification-templates-list .modal-body {
        height: auto;
    }
    @media (max-width: 1300px) {
        .sent-notification-modal .modal-body {
            height: calc(100vh - 230px);
            overflow-y: auto;
            overflow-x: hidden;
        }
        .sent-notification-modal .sent-notification-grid {
            height: calc(100vh - 320px);
        }
    }

    @media only screen and (max-width: 1366px) and (min-width: 1360px) {
        .sent-notification-modal .modal-body {
            height: calc(100vh - 200px);
            overflow-y: auto;
            overflow-x: hidden;
        }
        .sent-notification-modal .sent-notification-grid {
            height: calc(100vh - 290px);
        }
    }
</style>
